import React, { useState } from "react";
import {
    Grid,
    Typography,
    TextField,
    IconButton,
    Button,
    Box,
    Divider,
    Select,
    MenuItem,
    Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { CloudUpload } from "@mui/icons-material";
import { createBlobInContainer } from "src/utils/azureBlob";
import { convertUTCtoET } from "src/utils/timeUtils";

export default function EightBallTablesConfig({
    gameConfig,
    setGameConfig,
    loading = false,
    gameProviderId,
}) {
    const [uploadingTable, setUploadingTable] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(null);
    const [logoKeys, setLogoKeys] = useState({});

    const handleInputChange = (event, configIndex, tableIndex) => {
        const { name, value } = event.target;
        const newConfig = [...(gameConfig.config || [])];

        const parsedValue = ["commission", "entry", "maxFouls", "maxTime"].includes(
            name
        )
            ? parseFloat(value)
            : value;

        if (typeof tableIndex === "number") {
            newConfig[configIndex].tables[tableIndex] = {
                ...newConfig[configIndex].tables[tableIndex],
                [name]: parsedValue,
            };
        } else {
            newConfig[configIndex] = {
                ...newConfig[configIndex],
                [name]: parsedValue,
            };
        }

        setGameConfig({
            ...gameConfig,
            config: newConfig,
        });
    };

    const handleDeleteConfig = (index) => {
        const newConfig = [...(gameConfig.config || [])];
        newConfig.splice(index, 1);
        setGameConfig({
            ...gameConfig,
            config: newConfig,
        });
    };

    const handleDeleteTable = (configIndex, tableIndex) => {
        const newConfig = [...(gameConfig.config || [])];
        newConfig[configIndex].tables.splice(tableIndex, 1);
        setGameConfig({
            ...gameConfig,
            config: newConfig,
        });
    };

    const handleAddConfig = () => {
        const newConfig = [...(gameConfig.config || [])];
        const maxId = newConfig.length > 0
            ? Math.max(...newConfig.map(c => c.id || 0))
            : 0;
        newConfig.push({
            id: maxId + 1,
            startTime: "",
            endTime: "",
            tables: [],
        });
        setGameConfig({
            ...gameConfig,
            config: newConfig,
        });
    };

    const handleAddTable = (configIndex) => {
        const newConfig = [...(gameConfig.config || [])];
        newConfig[configIndex].tables.push({
            tableType: "",
            name: "",
            entry: 0,
            maxTime: 0,
            maxFouls: 0,
            commission: 0,
            prizeBreakdown: "",
        });
        setGameConfig({
            ...gameConfig,
            config: newConfig,
        });
    };

    const uploadTableLogo = async (event, tableType) => {
        if (event.target.files.length > 0) {
            const file = event.target.files[0];
            if (!file) return;

            const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
            const maxSize = 2 * 1024 * 1024;

            if (!validTypes.includes(file.type)) {
                setUploadError(`Only JPG, JPEG, PNG, WEBP, and GIF formats are allowed for ${tableType} table.`);
                event.target.value = null;
                return;
            }

            if (file.size > maxSize) {
                setUploadError(`File size should not exceed 2MB for ${tableType} table.`);
                event.target.value = null;
                return;
            }

            setUploadingTable(tableType);
            setUploadError(null);
            setUploadSuccess(null);
            try {
                const extension = file.name.substring(file.name.lastIndexOf("."));
                const fileName = `${tableType}${extension}`;
                const blobName = `gameprovider_${gameProviderId}/tables/${fileName}`;
                const renamedFile = new File([file], blobName, { type: file.type });

                await createBlobInContainer(renamedFile);

                setUploadSuccess(`Logo uploaded successfully for ${tableType} table!`);
                setLogoKeys(prev => ({ ...prev, [tableType]: Date.now() }));
                setTimeout(() => setUploadSuccess(null), 3000);
            } catch (err) {
                console.error(err);
                setUploadError(`Failed to upload logo for ${tableType} table`);
            } finally {
                setUploadingTable(null);
                event.target.value = null;
            }
        }
    };

    const getTableLogoUrl = (tableType) => {
        if (!gameProviderId || !tableType) return null;
        const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
        const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
        const key = logoKeys[tableType] || Date.now();
        return `https://${storageAccountName}.blob.core.windows.net/${containerName}/gameprovider_${gameProviderId}/tables/${tableType}.png?v=${key}`;
    };

    return (
        <Box>
            {uploadError && (
                <Alert severity="error" onClose={() => setUploadError(null)} sx={{ mb: 2 }}>
                    {uploadError}
                </Alert>
            )}
            {uploadSuccess && (
                <Alert severity="success" onClose={() => setUploadSuccess(null)} sx={{ mb: 2 }}>
                    {uploadSuccess}
                </Alert>
            )}
            <Box p={4}>
                {gameConfig?.config?.map((conf, configIndex) => (
                    <Box
                        key={conf.id}
                        sx={{
                            mb: 4,
                            p: 2,
                            border: "1px solid #ddd",
                            borderRadius: 2,
                            position: "relative",
                        }}
                    >
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={5}>
                                <TextField
                                    name="startTime"
                                    label="Start Time (UTC)"
                                    type="time"
                                    fullWidth
                                    value={conf.startTime}
                                    onChange={(e) => handleInputChange(e, configIndex)}
                                    disabled={loading}
                                    size="small"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    variant="outlined"
                                />
                                {conf.startTime && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            mt: 0.5,
                                            color: 'text.secondary',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        ET: {convertUTCtoET(conf.startTime)}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={5}>
                                <TextField
                                    name="endTime"
                                    label="End Time (UTC)"
                                    type="time"
                                    fullWidth
                                    value={conf.endTime}
                                    onChange={(e) => handleInputChange(e, configIndex)}
                                    disabled={loading}
                                    size="small"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    variant="outlined"
                                />
                                {conf.endTime && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            mt: 0.5,
                                            color: 'text.secondary',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        ET: {convertUTCtoET(conf.endTime)}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={2}>
                                <IconButton
                                    color="error"
                                    onClick={() => handleDeleteConfig(configIndex)}
                                    disabled={loading}
                                    sx={{ position: "absolute", right: -40, top: 170 }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle1" gutterBottom mb={2}>
                            Tables
                        </Typography>

                        {conf.tables.map((table, tableIndex) => (
                            <Grid
                                container
                                spacing={1}
                                alignItems="center"
                                key={tableIndex}
                                sx={{ mb: 1, mt: 2 }}
                            >
                                <Grid item xs={1}>
                                    <Select
                                        name="tableType"
                                        value={table.tableType}
                                        onChange={(e) =>
                                            handleInputChange(e, configIndex, tableIndex)
                                        }
                                        disabled={loading}
                                        size="small"
                                        fullWidth
                                    >
                                        {[
                                            ...new Set(
                                                gameConfig.config.flatMap((c) =>
                                                    c.tables.map((t) => t.tableType)
                                                )
                                            ),
                                        ]
                                            .filter(Boolean)
                                            .map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </Grid>
                                <Grid item xs={1.5}>
                                    <TextField
                                        name="name"
                                        label="Name"
                                        fullWidth
                                        value={table.name || ""}
                                        onChange={(e) =>
                                            handleInputChange(e, configIndex, tableIndex)
                                        }
                                        disabled={loading}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={1}>
                                    <TextField
                                        name="entry"
                                        label="Entry"
                                        type="number"
                                        fullWidth
                                        value={table.entry}
                                        onChange={(e) =>
                                            handleInputChange(e, configIndex, tableIndex)
                                        }
                                        disabled={loading}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={1.5}>
                                    <TextField
                                        name="maxTime"
                                        label="Max Time (sec.)"
                                        type="number"
                                        fullWidth
                                        value={table.maxTime}
                                        onChange={(e) =>
                                            handleInputChange(e, configIndex, tableIndex)
                                        }
                                        disabled={loading}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={1}>
                                    <TextField
                                        name="maxFouls"
                                        label="Max Fouls"
                                        type="number"
                                        fullWidth
                                        value={table.maxFouls}
                                        onChange={(e) =>
                                            handleInputChange(e, configIndex, tableIndex)
                                        }
                                        disabled={loading}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={1.5}>
                                    <TextField
                                        name="jackpotLimit"
                                        label="Jackpot Start"
                                        type="number"
                                        fullWidth
                                        value={table.jackpotLimit}
                                        onChange={(e) =>
                                            handleInputChange(e, configIndex, tableIndex)
                                        }
                                        disabled={loading}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextField
                                        name="commission"
                                        label="Commission %"
                                        type="number"
                                        fullWidth
                                        value={table.commission}
                                        onChange={(e) =>
                                            handleInputChange(e, configIndex, tableIndex)
                                        }
                                        disabled={loading}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextField
                                        name="prizeBreakdown"
                                        label="Prize Breakdown"
                                        fullWidth
                                        value={table.prizeBreakdown}
                                        onChange={(e) =>
                                            handleInputChange(e, configIndex, tableIndex)
                                        }
                                        disabled={loading}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={1}>
                                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                                        {table.tableType && (
                                            <Box
                                                component="img"
                                                src={getTableLogoUrl(table.tableType)}
                                                alt={`${table.tableType} logo`}
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    objectFit: "contain",
                                                    border: "1px solid #ddd",
                                                    borderRadius: 0.5,
                                                    display: "block",
                                                }}
                                                onError={(e) => {
                                                    e.target.style.display = "none";
                                                }}
                                                onLoad={(e) => {
                                                    e.target.style.display = "block";
                                                }}
                                                key={logoKeys[table.tableType] || "default"}
                                            />
                                        )}
                                        {table.tableType && (
                                            <>
                                                <input
                                                    accept="image/*"
                                                    style={{ display: "none" }}
                                                    id={`upload-table-logo-${configIndex}-${tableIndex}`}
                                                    type="file"
                                                    onChange={(e) => uploadTableLogo(e, table.tableType)}
                                                    disabled={uploadingTable === table.tableType || !gameProviderId}
                                                />
                                                <label htmlFor={`upload-table-logo-${configIndex}-${tableIndex}`}>
                                                    <IconButton
                                                        component="span"
                                                        size="small"
                                                        disabled={uploadingTable === table.tableType || !gameProviderId}
                                                        color="primary"
                                                    >
                                                        <CloudUpload fontSize="small" />
                                                    </IconButton>
                                                </label>
                                            </>
                                        )}
                                        <IconButton
                                            color="error"
                                            onClick={() => handleDeleteTable(configIndex, tableIndex)}
                                            disabled={loading}
                                            size="small"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            </Grid>
                        ))}

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleAddTable(configIndex)}
                            disabled={loading}
                            sx={{ mt: 1 }}
                        >
                            Add Table
                        </Button>
                    </Box>
                ))}
            </Box>

            <Button
                variant="contained"
                color="primary"
                onClick={handleAddConfig}
                disabled={loading}
            >
                + Add Configuration
            </Button>
        </Box>
    );
}
